import { useEffect, useRef, useState } from "react";
import { Carousel } from "react-swipeable-scroll-snap-carousel";

import { Button, Menu, MenuItem } from "@mui/material";

import { Image } from "@models/Image";
import cld, { CldImage, CldVideo } from "@services/cloudinary";
import { CldVideoRef } from "@services/cloudinary/CldVideo";
import useHotKey from "@utils/useHotKey";

import FaIcon from "./FaIcon";

type Slide = Image & {
  pageNumber: number;
  component: (props: { isActive: boolean }) => React.ReactNode;
  downloadOptions: Record<string, string> | null;
};

const VideoSlide = ({
  cloudinaryId,
  id,
  isActive,
}: {
  cloudinaryId: string;
  id: string;
  isActive: boolean;
}) => {
  const videoRef = useRef<CldVideoRef>(null);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        videoRef.current?.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            console.warn(`Video ${id} autoplay was blocked by the browser.`);
          } else {
            console.error("Video play error:", error);
          }
        });
      }, 50);
      return () => clearTimeout(timer);
    } else {
      videoRef.current?.pause();
    }
  }, [isActive, id]);

  return (
    <CldVideo
      key={id}
      cloudinaryId={cloudinaryId}
      ref={videoRef}
      className="pointer-events-auto cursor-auto w-full"
    />
  );
};

const makeSlide = (file: Image): Slide | Slide[] => {
  const { id, cloudinaryId, pdfPageCount, type } = file;
  if (type === "video") {
    return {
      ...file,
      pageNumber: 1,
      component: ({ isActive }) => (
        <VideoSlide cloudinaryId={cloudinaryId} id={id} isActive={isActive} />
      ),
      downloadOptions: null,
    };
  }
  return Array.from({ length: pdfPageCount || 1 }, (_, i) => ({
    ...file,
    pageNumber: i + 1,
    downloadOptions:
      pdfPageCount > 1
        ? {
            "Download Original PDF": getImageDownloadUrl(cloudinaryId),
            "Download Slide as JPG": getImageDownloadUrl(
              cloudinaryId + ".jpg",
              i + 1
            ),
          }
        : {
            "Download as JPG": getImageDownloadUrl(cloudinaryId + ".jpg"),
            "Download as PDF": getImageDownloadUrl(cloudinaryId + ".pdf"),
          },
    component: () => (
      <CldImage
        publicId={cloudinaryId}
        page={i + 1}
        size="large"
        draggable={false}
      />
    ),
  })) as unknown as Slide[];
};

const getImageDownloadUrl = (cloudinaryId: string, page?: number) => {
  return cld.url(cloudinaryId, {
    resource_type: "image",
    secure: true,
    flags: "attachment",
    page: page || undefined,
  });
};

const DownloadMenu = ({
  downloadOptions,
}: {
  downloadOptions: Record<string, string>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        onClick={handleClick}
        startIcon={<FaIcon icon="arrow-down-to-line" />}
        className="bg-primary-50 px-4"
      >
        Download
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {Object.entries(downloadOptions).map(([label, url]) => (
          <MenuItem
            component="a"
            key={label}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ImageCarousel = ({
  images,
  startImageId,
  startIndex,
}: {
  images: Image[];
  startImageId?: string;
  startIndex?: number;
}) => {
  const slides = images
    .filter((image) => image.type !== "print-ready")
    .flatMap(makeSlide);

  const indexById =
    startImageId !== undefined
      ? slides.findIndex((slide) => slide.id === startImageId)
      : -1;
  const startSlideIndex = indexById !== -1 ? indexById : (startIndex ?? 0);

  const [activeStep, setActiveStep] = useState(startSlideIndex);

  const activeSlide = slides[activeStep];

  useHotKey("ArrowLeft", () => setActiveStep((step) => Math.max(step - 1, 0)));
  useHotKey("ArrowRight", () =>
    setActiveStep((step) => Math.min(step + 1, slides.length - 1))
  );

  return (
    <div className="flex flex-col gap-2 max-w-full">
      <div className="min-h-80 min-w-80 flex justify-center items-center">
        <Carousel onChangeIndex={setActiveStep} index={activeStep}>
          {slides.map((slide, i) => (
            <div
              key={slide.id ? `${slide.id}-${slide.pageNumber}` : i}
              className="flex justify-center h-full items-center"
            >
              {slide.component({ isActive: i === activeStep })}
            </div>
          ))}
        </Carousel>
      </div>
      <div className="flex justify-between gap-4">
        <div className="flex gap-2 items-baseline shrink-0">
          {activeSlide?.downloadOptions && (
            <DownloadMenu downloadOptions={activeSlide.downloadOptions} />
          )}
          {activeSlide?.pdfPageCount > 1 && (
            <p className="text-xs text-neutral-400">
              PDF page {activeSlide.pageNumber} of {activeSlide.pdfPageCount}
            </p>
          )}
        </div>
        {slides.length > 1 && (
          <div className="flex flex-wrap gap-3">
            {slides.map((slide, i) => (
              <button
                key={
                  slide.id
                    ? `thumb-${slide.id}-${slide.pageNumber}`
                    : `thumb-${i}`
                }
                onClick={() => setActiveStep(i)}
              >
                <CldImage
                  publicId={slide.cloudinaryId}
                  page={slide.pageNumber}
                  size="thumbnail"
                  resourceType={slide.type === "video" ? "video" : "image"}
                  draggable={false}
                  className={`object-cover rounded w-14 h-14 ${
                    activeStep === i
                      ? "outline outline-2 outline-primary-500 outline-offset-2"
                      : ""
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageCarousel;
